if (!customElements.get("collect-monsters-header")) {
  class CollectMonstersHeader extends HTMLElement {
    constructor() {
      super();

      this.desktopQuery = window.matchMedia("(min-width: 834px)");
      this.reducedMotionQuery = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      // Capture-derived pacing: desktop intent/handoff and mobile shell/level
      // completion each remain independent so one generic duration cannot flatten
      // the navigation's interaction character.
      this.timings = Object.freeze({
        hoverOpen: 130,
        hoverSwitch: 170,
        desktopSwitchGap: 134,
        desktopSwitchSettle: 240,
        desktopClose: 400,
        mobileShell: 510,
        mobileLevel: 410,
        mobileFocus: 430,
      });
      this.activePanel = null;
      this.outgoingPanel = null;
      this.pendingPanel = null;
      this.pendingTrigger = null;
      this.pendingNavigationSwitch = false;
      this.switchFromHeight = null;
      this.lastTrigger = null;
      this.mobileReturnButton = null;
      this.closeTimer = null;
      this.switchTimer = null;
      this.switchCleanupTimer = null;
      this.mobileTimer = null;
      this.mobileBackTimer = null;
      this.mobileLevelTimer = null;
      this.hoverTimer = null;
      this.hoverTrigger = null;
      this.searchDebounceTimer = null;
      this.searchAbortController = null;
      this.searchRequestId = 0;
      this.bagRefreshRequestId = 0;
      this.bagContentCurrent = true;
      this.searchCache = new Map();
      this.panelHideTimers = new Map();
      this.searchActiveIndex = -1;
      this.activationPointerType = null;
      this.scrollLockActive = false;
      this.lockedScrollY = 0;

      this.handleClick = this.handleClick.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerOver = this.handlePointerOver.bind(this);
      this.handleDesktopRegionEnter = this.handleDesktopRegionEnter.bind(this);
      this.handleDesktopRegionLeave = this.handleDesktopRegionLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleCartUpdated = this.handleCartUpdated.bind(this);
    }

    connectedCallback() {
      this.panelTriggers = [
        ...this.querySelectorAll("[data-cm-panel-trigger]"),
      ];
      this.panels = [...this.querySelectorAll("[data-cm-panel]")];
      this.searchTrigger = this.querySelector("[data-cm-search-trigger]");
      this.bagTrigger = this.querySelector("[data-cm-bag-trigger]");
      this.bagPanel = this.querySelector("[data-cm-bag-panel]");
      this.searchPanel = this.querySelector("[data-cm-search-panel]");
      this.searchInput = this.querySelector("[data-cm-search-input]");
      this.searchClear = this.querySelector("[data-cm-search-clear]");
      this.searchDefault = this.querySelector("[data-cm-search-default]");
      this.searchResults = this.querySelector("[data-cm-search-results]");
      this.searchStatus = this.querySelector("[data-cm-search-status]");
      this.storefrontSearchUrl = this.getAttribute(
        "data-storefront-search-url",
      );
      this.curtain = this.querySelector("[data-cm-curtain]");
      this.desktopBar = this.querySelector(".cm-header__bar");
      this.desktopPanels = this.querySelector(".cm-header__desktop-panels");
      this.mobileToggle = this.querySelector("[data-cm-mobile-toggle]");
      this.mobileMenuId =
        this.mobileToggle?.getAttribute("aria-controls") || "";
      this.mobileBack = this.querySelector(
        ".cm-header__mobile-back[data-cm-mobile-back]",
      );
      this.mobileMenu = this.querySelector("[data-cm-mobile-menu]");
      this.mobileLevels = [...this.querySelectorAll("[data-cm-mobile-level]")];
      this.allPanels = [...this.panels];
      if (this.searchPanel) this.allPanels.push(this.searchPanel);
      if (this.bagPanel) this.allPanels.push(this.bagPanel);

      this.resetInterface();

      this.addEventListener("click", this.handleClick);
      this.addEventListener("pointerdown", this.handlePointerDown, {
        passive: true,
      });
      this.addEventListener("pointerover", this.handlePointerOver);
      this.addEventListener("focusin", this.handleFocusIn);
      this.addEventListener("focusout", this.handleFocusOut);
      this.addEventListener("input", this.handleInput);
      this.addEventListener("keydown", this.handleKeydown);
      this.desktopBar?.addEventListener(
        "pointerenter",
        this.handleDesktopRegionEnter,
      );
      this.desktopBar?.addEventListener(
        "pointerleave",
        this.handleDesktopRegionLeave,
      );
      this.allPanels.forEach((panel) => {
        panel.addEventListener("pointerenter", this.handleDesktopRegionEnter);
        panel.addEventListener("pointerleave", this.handleDesktopRegionLeave);
      });
      document.addEventListener("click", this.handleDocumentClick);
      window.addEventListener("scroll", this.handleScroll, { passive: true });
      window.addEventListener("resize", this.handleResize, { passive: true });
      document.addEventListener("cm:cart-updated", this.handleCartUpdated);

      if (this.desktopQuery.addEventListener) {
        this.desktopQuery.addEventListener(
          "change",
          this.handleBreakpointChange,
        );
      } else {
        this.desktopQuery.addListener(this.handleBreakpointChange);
      }

      this.handleScroll();
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
      this.removeEventListener("pointerdown", this.handlePointerDown);
      this.removeEventListener("pointerover", this.handlePointerOver);
      this.removeEventListener("focusin", this.handleFocusIn);
      this.removeEventListener("focusout", this.handleFocusOut);
      this.removeEventListener("input", this.handleInput);
      this.removeEventListener("keydown", this.handleKeydown);
      this.desktopBar?.removeEventListener(
        "pointerenter",
        this.handleDesktopRegionEnter,
      );
      this.desktopBar?.removeEventListener(
        "pointerleave",
        this.handleDesktopRegionLeave,
      );
      this.allPanels?.forEach((panel) => {
        panel.removeEventListener(
          "pointerenter",
          this.handleDesktopRegionEnter,
        );
        panel.removeEventListener(
          "pointerleave",
          this.handleDesktopRegionLeave,
        );
      });
      document.removeEventListener("click", this.handleDocumentClick);
      window.removeEventListener("scroll", this.handleScroll);
      window.removeEventListener("resize", this.handleResize);
      document.removeEventListener("cm:cart-updated", this.handleCartUpdated);

      if (this.desktopQuery.removeEventListener) {
        this.desktopQuery.removeEventListener(
          "change",
          this.handleBreakpointChange,
        );
      } else {
        this.desktopQuery.removeListener(this.handleBreakpointChange);
      }

      this.clearTimers();
      this.searchAbortController?.abort();
      this.bagRefreshRequestId += 1;
      this.setScrollLock(false);
    }

    resetInterface() {
      this.clearTimers();

      this.allPanels?.forEach((panel) => {
        panel.classList.remove("is-active", "is-switching-out");
        panel.hidden = true;
        panel.removeAttribute("inert");
        panel.removeAttribute("aria-hidden");
        panel.setAttribute("role", "region");
        panel.removeAttribute("aria-modal");
        panel.style.removeProperty("height");
        panel.style.removeProperty("--cm-panel-open-height");
      });

      this.panelTriggers?.forEach((trigger) => {
        trigger.setAttribute("aria-expanded", "false");
      });
      this.searchTrigger?.setAttribute("aria-expanded", "false");
      this.syncBagTriggerMode(false);
      this.resetSearchSession(false);

      if (this.curtain) {
        this.curtain.classList.remove("is-active");
        this.curtain.hidden = true;
      }
      if (this.mobileMenu) {
        this.mobileMenu.hidden = true;
        this.mobileMenu.setAttribute("inert", "");
        this.mobileMenu.setAttribute("aria-hidden", "true");
      }
      if (this.mobileToggle) {
        this.setMobileToggleState(
          this.mobileMenuId,
          "Open navigation menu",
          false,
        );
      }
      if (this.mobileBack) this.mobileBack.hidden = true;

      this.classList.remove(
        "is-panel-open",
        "is-panel-closing",
        "is-panel-switching",
        "is-navigation-switching",
        "is-mobile-open",
        "is-mobile-submenu-open",
        "is-mobile-search-open",
        "is-mobile-bag-open",
        "is-mobile-utility-closing",
        "is-mobile-level-transitioning",
      );
      this.removeAttribute("data-cm-mobile-direction");
      this.activePanel = null;
      this.outgoingPanel = null;
      this.pendingPanel = null;
      this.pendingTrigger = null;
      this.pendingNavigationSwitch = false;
      this.switchFromHeight = null;
      this.lastTrigger = null;
      this.mobileReturnButton = null;
      this.searchActiveIndex = -1;
      this.activationPointerType = null;
      this.desktopPanels?.style.removeProperty("--cm-panel-shell-height");
      this.syncSearchClear();
      this.setMobileRootState();
      this.setScrollLock(false);
    }

    clearTimers() {
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.switchTimer);
      window.clearTimeout(this.switchCleanupTimer);
      window.clearTimeout(this.mobileTimer);
      window.clearTimeout(this.mobileBackTimer);
      window.clearTimeout(this.mobileLevelTimer);
      window.clearTimeout(this.hoverTimer);
      window.clearTimeout(this.searchDebounceTimer);
      this.hoverTrigger = null;
      this.panelHideTimers.forEach((timer) => {
        window.clearTimeout(timer);
      });
      this.panelHideTimers.clear();
    }

    duration(milliseconds) {
      return this.reducedMotionQuery.matches ? 0 : milliseconds;
    }

    cssPixelValue(property, fallback) {
      const value = Number.parseFloat(
        window.getComputedStyle(this).getPropertyValue(property),
      );
      return Number.isFinite(value) ? value : fallback;
    }

    desktopHeight() {
      return this.cssPixelValue("--cm-header-desktop-height", 44);
    }

    mobileHeight() {
      return this.cssPixelValue("--cm-header-mobile-height", 48);
    }

    setScrollLock(locked) {
      if (locked === this.scrollLockActive) return;

      if (locked) {
        this.lockedScrollY = window.scrollY;
        const bodyPaddingRight = Number.parseFloat(
          window.getComputedStyle(document.body).paddingRight,
        );
        const scrollbarWidth = Math.max(
          0,
          window.innerWidth - document.documentElement.clientWidth,
        );
        document.body.style.setProperty(
          "--cm-header-lock-scroll-y",
          `${this.lockedScrollY}px`,
        );
        document.body.style.setProperty(
          "--cm-header-lock-body-padding-right",
          `${Number.isFinite(bodyPaddingRight) ? bodyPaddingRight : 0}px`,
        );
        document.body.style.setProperty(
          "--cm-header-lock-scrollbar-width",
          `${scrollbarWidth}px`,
        );
        document.documentElement.classList.add("cm-header-lock");
        document.body.classList.add("cm-header-lock");
        this.scrollLockActive = true;
        return;
      }

      document.documentElement.classList.remove("cm-header-lock");
      document.body.classList.remove("cm-header-lock");
      document.body.style.removeProperty("--cm-header-lock-scroll-y");
      document.body.style.removeProperty(
        "--cm-header-lock-body-padding-right",
      );
      document.body.style.removeProperty("--cm-header-lock-scrollbar-width");
      this.scrollLockActive = false;
      window.scrollTo(0, this.lockedScrollY);
    }

    syncBagTriggerMode(expanded = false) {
      if (!this.bagTrigger) return;
      if (this.bagPanel?.id) {
        this.bagTrigger.setAttribute("aria-controls", this.bagPanel.id);
        this.bagTrigger.setAttribute("aria-expanded", String(expanded));
      }
    }

    async handleCartUpdated(event) {
      const detail = event.detail || {};
      const cart = detail.cart;
      const itemCount = Number(cart?.item_count);
      const requestId = ++this.bagRefreshRequestId;

      this.bagContentCurrent = false;
      this.closeBagPreviewForRefresh();

      try {
        const refreshed = await this.refreshBagSection(
          requestId,
          detail.sections,
          detail.sectionId,
        );
        if (!refreshed || requestId !== this.bagRefreshRequestId) return;
        this.bagContentCurrent = true;
        if (detail.openBag === true) this.openBagAfterCartUpdate();
      } catch (sectionError) {
        if (requestId !== this.bagRefreshRequestId) return;
        if (Number.isInteger(itemCount) && itemCount >= 0) {
          this.updateBagCount(itemCount);
        } else {
          try {
            const response = await fetch(
              `${window.Shopify?.routes?.root || "/"}cart.js`,
              { headers: { Accept: "application/json" } },
            );
            const refreshedCart = await response.json();
            if (requestId !== this.bagRefreshRequestId) return;
            const refreshedCount = Number(refreshedCart?.item_count);
            if (
              response.ok &&
              Number.isInteger(refreshedCount) &&
              refreshedCount >= 0
            ) {
              this.updateBagCount(refreshedCount);
            }
          } catch {
            // The next navigation or page refresh will restore server-rendered state.
          }
        }
        console.warn("Bag preview could not be refreshed.", sectionError);
      }
    }

    closeBagPreviewForRefresh() {
      if (!this.bagPanel) return;
      const focusInside = this.bagPanel.contains(document.activeElement);

      if (this.desktopQuery.matches) {
        if (
          this.activePanel === this.bagPanel ||
          this.outgoingPanel === this.bagPanel ||
          this.pendingPanel === this.bagPanel
        ) {
          this.closeDesktopPanel(focusInside, true);
        }
        return;
      }

      if (
        this.classList.contains("is-mobile-bag-open") ||
        this.activePanel === this.bagPanel
      ) {
        this.closeMobileBag(focusInside, true);
      }
    }

    openBagAfterCartUpdate() {
      if (
        !this.bagContentCurrent ||
        !this.bagPanel ||
        !this.bagTrigger
      ) {
        return;
      }

      if (this.desktopQuery.matches) {
        this.showDesktopPanel(this.bagPanel, this.bagTrigger);
        // Keep Escape and keyboard navigation inside the existing Header
        // interaction model after the Product Page initiated the opening.
        this.bagTrigger.focus({ preventScroll: true });
      } else {
        this.openMobileBag();
      }
    }

    updateBagCount(itemCount) {
      const count = this.querySelector("[data-cm-bag-count]");
      if (count) {
        const previousCount = count.hidden ? 0 : Number(count.textContent);
        const changed = previousCount !== itemCount;
        count.textContent = String(itemCount);
        count.hidden = itemCount === 0;

        if (changed && itemCount > 0) {
          count.classList.remove("is-updating");
          void count.offsetWidth;
          count.classList.add("is-updating");
        }
      }

      if (this.bagTrigger) {
        let label = this.bagTrigger.dataset.cmBagLabelEmpty || "";
        if (itemCount === 1) {
          label = this.bagTrigger.dataset.cmBagLabelOne || label;
        } else if (itemCount > 1) {
          label = (this.bagTrigger.dataset.cmBagLabelOther || label).replace(
            "__COUNT__",
            String(itemCount),
          );
        }
        if (label) this.bagTrigger.setAttribute("aria-label", label);
      }
    }

    async refreshBagSection(requestId, bundledSections, bundledSectionId) {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) throw new Error("Missing header section ID.");

      let html =
        bundledSectionId === sectionId ? bundledSections?.[sectionId] : null;

      if (!html) {
        const rootPath = window.Shopify?.routes?.root || "/";
        const url = new URL(rootPath, window.location.origin);
        url.searchParams.set("sections", sectionId);
        url.searchParams.set("_cm_bag_refresh", String(requestId));
        const response = await fetch(url, {
          cache: "no-store",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(
            `Header section request failed (${response.status}).`,
          );
        }

        const sections = await response.json();
        if (requestId !== this.bagRefreshRequestId) return false;
        html = sections?.[sectionId];
      }

      if (requestId !== this.bagRefreshRequestId) return false;
      if (!html) throw new Error("Header section did not render.");

      const parsed = new DOMParser().parseFromString(html, "text/html");
      const freshHeader = parsed.querySelector("collect-monsters-header");
      const freshContent = freshHeader?.querySelector("[data-cm-bag-content]");
      const currentContent = this.querySelector("[data-cm-bag-content]");
      if (!freshHeader || !freshContent || !currentContent) {
        throw new Error("Header Bag markup was incomplete.");
      }

      const freshCount = freshHeader.querySelector("[data-cm-bag-count]");
      const renderedCount = Number(freshCount?.textContent || 0);
      if (Number.isInteger(renderedCount) && renderedCount >= 0) {
        this.updateBagCount(renderedCount);
      }

      const freshTrigger = freshHeader.querySelector("[data-cm-bag-trigger]");
      const renderedLabel = freshTrigger?.getAttribute("aria-label");
      if (renderedLabel && this.bagTrigger) {
        this.bagTrigger.setAttribute("aria-label", renderedLabel);
      }

      const focusedElement = document.activeElement;
      const restoreBagFocus = currentContent.contains(focusedElement);
      const focusedHref = focusedElement
        ?.closest?.("a[href]")
        ?.getAttribute("href");
      currentContent.replaceWith(freshContent);
      if (restoreBagFocus) {
        window.requestAnimationFrame(() => {
          const links = [...freshContent.querySelectorAll("a[href]")];
          const focusTarget =
            links.find((link) => link.getAttribute("href") === focusedHref) ||
            links[0];
          focusTarget?.focus({ preventScroll: true });
        });
      }
      this.updateActivePanelHeight();
      return true;
    }

    handleClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const activationPointerType = this.getActivationPointerType(event);

      const mobileToggle = target.closest("[data-cm-mobile-toggle]");
      if (mobileToggle && this.contains(mobileToggle)) {
        event.preventDefault();
        if (this.isMobileUtilityOpen()) {
          this.closeMobileUtility(true);
        } else {
          this.toggleMobileMenu();
        }
        return;
      }

      const mobileBack = target.closest("[data-cm-mobile-back]");
      if (mobileBack && this.contains(mobileBack)) {
        event.preventDefault();
        this.openMobileRoot(true);
        return;
      }

      const mobileOpen = target.closest("[data-cm-mobile-open]");
      if (mobileOpen && this.contains(mobileOpen)) {
        event.preventDefault();
        this.openMobileLevel(mobileOpen);
        return;
      }

      const searchClear = target.closest("[data-cm-search-clear]");
      if (searchClear && this.contains(searchClear)) {
        event.preventDefault();
        this.clearSearchQuery();
        return;
      }

      const panelTrigger = target.closest("[data-cm-panel-trigger]");
      if (
        panelTrigger &&
        this.contains(panelTrigger) &&
        this.desktopQuery.matches
      ) {
        event.preventDefault();
        const panelId = panelTrigger.getAttribute("aria-controls");
        const panel = panelId ? document.getElementById(panelId) : null;
        if (
          panel &&
          (this.activePanel === panel || this.pendingPanel === panel)
        ) {
          this.closeDesktopPanel(true);
        } else {
          this.openDesktopPanel(panelTrigger);
        }
        return;
      }

      const bagTrigger = target.closest("[data-cm-bag-trigger]");
      if (bagTrigger && this.contains(bagTrigger)) {
        // A failed refresh leaves the anchor's native /cart navigation as the
        // truthful fallback instead of opening a known-stale preview.
        if (!this.bagContentCurrent) return;

        if (!this.desktopQuery.matches) {
          event.preventDefault();
          if (this.classList.contains("is-mobile-bag-open")) {
            this.closeMobileBag(true);
          } else {
            this.openMobileBag();
          }
          return;
        }

        // A wide touch device keeps the desktop layout, but the Bag anchor must
        // retain its direct one-tap navigation path. Mouse and keyboard users
        // get the Bag preview, which contains the canonical cart link.
        if (activationPointerType && activationPointerType !== "mouse") return;

        if (
          this.activePanel === this.bagPanel ||
          this.pendingPanel === this.bagPanel
        ) {
          event.preventDefault();
          this.closeDesktopPanel(true);
          return;
        }
        event.preventDefault();
        this.showDesktopPanel(this.bagPanel, this.bagTrigger);
        return;
      }

      const searchTrigger = target.closest("[data-cm-search-trigger]");
      if (searchTrigger && this.contains(searchTrigger)) {
        event.preventDefault();
        if (this.desktopQuery.matches) {
          if (
            this.activePanel === this.searchPanel ||
            this.pendingPanel === this.searchPanel
          ) {
            this.closeDesktopPanel(true);
          } else {
            this.openSearchPanel();
          }
        } else if (this.classList.contains("is-mobile-search-open")) {
          this.closeMobileSearch(true);
        } else {
          this.openMobileSearch();
        }
        return;
      }

      const curtain = target.closest("[data-cm-curtain]");
      if (curtain && this.contains(curtain)) {
        event.preventDefault();
        this.closeDesktopPanel(true);
      }
    }

    handlePointerDown(event) {
      this.activationPointerType = event.pointerType || null;
    }

    getActivationPointerType(event) {
      const pointerType = event.pointerType || this.activationPointerType || "";
      this.activationPointerType = null;
      return pointerType;
    }

    isHoverPointer(event) {
      return event.pointerType === "mouse";
    }

    handlePointerOver(event) {
      if (!this.desktopQuery.matches || !this.isHoverPointer(event)) return;

      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const navigationTarget = target.closest(
        ".cm-header__nav-item, .cm-header__brand, .cm-header__action",
      );

      if (!navigationTarget || !this.contains(navigationTarget)) {
        if (this.desktopBar?.contains(target)) {
          this.cancelHoverIntent();
          this.closeUnlessSearchFocused();
        }
        return;
      }
      if (
        event.relatedTarget instanceof Node &&
        navigationTarget.contains(event.relatedTarget)
      ) {
        return;
      }

      const trigger = navigationTarget.matches(".cm-header__nav-item")
        ? navigationTarget.querySelector("[data-cm-panel-trigger]")
        : null;
      if (trigger) {
        this.scheduleHoverIntent(trigger);
      } else if (
        (navigationTarget === this.searchTrigger &&
          this.activePanel === this.searchPanel) ||
        (navigationTarget === this.bagTrigger &&
          this.activePanel === this.bagPanel)
      ) {
        this.cancelHoverIntent();
        this.cancelDesktopClose();
      } else {
        this.cancelHoverIntent();
        this.closeDesktopPanel(false);
      }
    }

    scheduleHoverIntent(trigger) {
      if (!trigger || trigger === this.hoverTrigger) return;

      this.cancelHoverIntent();
      const panelId = trigger.getAttribute("aria-controls");
      const panel = panelId ? document.getElementById(panelId) : null;
      if (!panel || !this.contains(panel)) return;
      if (panel === this.activePanel || panel === this.pendingPanel) {
        this.cancelDesktopClose();
        return;
      }

      this.hoverTrigger = trigger;
      const delay = this.activePanel || this.outgoingPanel || this.pendingPanel
        ? this.timings.hoverSwitch
        : this.timings.hoverOpen;
      this.hoverTimer = window.setTimeout(() => {
        if (this.hoverTrigger !== trigger || !this.desktopQuery.matches) return;
        this.hoverTrigger = null;
        this.openDesktopPanel(trigger);
      }, this.duration(delay));
    }

    cancelHoverIntent() {
      window.clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
      this.hoverTrigger = null;
    }

    closeUnlessSearchFocused() {
      if (
        this.activePanel === this.searchPanel &&
        this.searchPanel?.contains(document.activeElement)
      ) {
        return;
      }
      this.closeDesktopPanel(false);
    }

    handleDesktopRegionEnter(event) {
      if (!this.isHoverPointer(event)) return;
      this.cancelDesktopClose();
    }

    handleDesktopRegionLeave(event) {
      if (!this.desktopQuery.matches || !this.isHoverPointer(event)) {
        return;
      }

      this.cancelHoverIntent();
      if (!this.activePanel && !this.outgoingPanel && !this.pendingPanel) return;

      const nextTarget = event.relatedTarget;
      if (
        nextTarget instanceof Node &&
        (this.desktopBar?.contains(nextTarget) ||
          this.activePanel?.contains(nextTarget) ||
          this.outgoingPanel?.contains(nextTarget))
      ) {
        return;
      }
      this.closeDesktopPanel(false);
    }

    handleFocusIn(event) {
      if (!this.desktopQuery.matches) return;

      const target = event.target instanceof Element ? event.target : null;
      if (target && this.activePanel?.contains(target)) {
        this.cancelDesktopClose();
        return;
      }

      const trigger = target?.closest("[data-cm-panel-trigger]");
      if (trigger && this.contains(trigger)) {
        const panelId = trigger.getAttribute("aria-controls");
        const panel = panelId ? document.getElementById(panelId) : null;
        if (
          panel &&
          (this.activePanel === panel || this.pendingPanel === panel)
        ) {
          this.cancelDesktopClose();
        } else {
          this.closeUnlessSearchFocused();
        }
      } else if (
        this.bagPanel &&
        target?.closest("[data-cm-bag-trigger]") === this.bagTrigger
      ) {
        if (this.activePanel === this.bagPanel) this.cancelDesktopClose();
        else this.closeUnlessSearchFocused();
      } else if (target && this.desktopBar?.contains(target)) {
        this.closeUnlessSearchFocused();
      }
    }

    handleFocusOut() {
      window.setTimeout(() => {
        if (
          !this.desktopQuery.matches ||
          (!this.activePanel && !this.outgoingPanel && !this.pendingPanel)
        )
          return;
        if (this.classList.contains("is-panel-switching")) return;
        const focused = document.activeElement;
        if (
          !(focused instanceof Node) ||
          (!this.desktopBar?.contains(focused) &&
            !this.activePanel?.contains(focused) &&
            !this.outgoingPanel?.contains(focused))
        ) {
          this.closeDesktopPanel(false);
        }
      }, 0);
    }

    handleInput(event) {
      if (event.target !== this.searchInput) return;

      this.cancelDesktopClose();
      window.clearTimeout(this.searchDebounceTimer);
      this.resetSearchSelection();
      this.syncSearchClear();

      const searchTerm = this.searchInput.value.trim();
      if (!searchTerm) {
        this.clearPredictiveResults(true, true);
        return;
      }

      if (this.searchDefault) this.searchDefault.hidden = true;
      this.clearPredictiveResults(false, false);
      this.setSearchStatus("Finding related products…");
      this.searchDebounceTimer = window.setTimeout(
        () => this.getPredictiveResults(searchTerm),
        220,
      );
    }

    handleDocumentClick(event) {
      if (
        this.desktopQuery.matches &&
        (this.activePanel || this.outgoingPanel || this.pendingPanel) &&
        !this.contains(event.target)
      ) {
        this.closeDesktopPanel(false);
      }
    }

    handleScroll() {
      const scrollY = this.scrollLockActive
        ? this.lockedScrollY
        : window.scrollY;
      this.classList.toggle("is-scrolled", scrollY > 8);
    }

    handleResize() {
      if (this.desktopQuery.matches && this.activePanel) {
        this.setDesktopShellHeight(this.measurePanel(this.activePanel));
      } else if (this.desktopQuery.matches && this.outgoingPanel) {
        const outgoingHeight = this.outgoingPanel.getBoundingClientRect().height;
        this.switchFromHeight = outgoingHeight;
        this.setDesktopShellHeight(outgoingHeight);
      }
    }

    handleBreakpointChange() {
      this.activationPointerType = null;
      this.closeDesktopPanel(false, true);
      this.closeMobileMenu(false, true);
      this.closeMobileUtility(false, true);
      this.syncBagTriggerMode(false);
    }

    handleKeydown(event) {
      if (event.key === "Escape") {
        if (this.isMobileUtilityOpen()) {
          event.preventDefault();
          this.closeMobileUtility(true);
        } else if (this.classList.contains("is-mobile-open")) {
          event.preventDefault();
          this.closeMobileMenu(true);
        } else if (
          this.activePanel ||
          this.outgoingPanel ||
          this.pendingPanel ||
          this.classList.contains("is-panel-open")
        ) {
          event.preventDefault();
          this.closeDesktopPanel(true);
        }
        return;
      }

      if (event.target === this.searchInput) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          this.moveSearchSelection(1);
          return;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          this.moveSearchSelection(-1);
          return;
        }
        if (event.key === "Enter" && this.searchActiveIndex >= 0) {
          const option = this.getSearchOptions()[this.searchActiveIndex];
          if (option) {
            event.preventDefault();
            option.click();
          }
        }
      }

      if (
        event.key === "Tab" &&
        this.desktopQuery.matches &&
        this.activePanel &&
        this.activePanel !== this.searchPanel &&
        this.handleDesktopPanelTab(event)
      ) {
        return;
      }

      if (
        event.key === "Tab" &&
        (this.classList.contains("is-mobile-open") ||
          this.isMobileUtilityOpen())
      ) {
        this.trapMobileFocus(event);
      }
    }

    openDesktopPanel(trigger) {
      if (!this.desktopQuery.matches) return;
      const panelId = trigger.getAttribute("aria-controls");
      const panel = panelId ? document.getElementById(panelId) : null;
      if (!panel || !this.contains(panel)) return;
      this.showDesktopPanel(panel, trigger);
    }

    handleDesktopPanelTab(event) {
      const panel = this.activePanel;
      const trigger = this.lastTrigger;
      if (!panel || !trigger) return false;

      const panelControls = [
        ...panel.querySelectorAll("a[href], button:not([disabled])"),
      ].filter((element) => this.isVisible(element));
      if (!panelControls.length) return false;

      const first = panelControls[0];
      const last = panelControls[panelControls.length - 1];
      const focused = document.activeElement;

      if (!event.shiftKey && focused === trigger) {
        event.preventDefault();
        first.focus();
        return true;
      }

      if (event.shiftKey && focused === first) {
        event.preventDefault();
        trigger.focus();
        return true;
      }

      if (!event.shiftKey && focused === last) {
        const headerControls = [
          ...this.desktopBar.querySelectorAll(
            "a[href], button:not([disabled])",
          ),
        ].filter((element) => this.isVisible(element));
        const triggerIndex = headerControls.indexOf(trigger);
        const nextControl = headerControls[triggerIndex + 1];
        if (nextControl) {
          event.preventDefault();
          nextControl.focus();
          return true;
        }
      }

      return false;
    }

    openSearchPanel() {
      if (!this.searchPanel || !this.searchTrigger) return;
      if (!this.desktopQuery.matches) {
        this.openMobileSearch();
        return;
      }

      this.showDesktopPanel(this.searchPanel, this.searchTrigger);
    }

    showDesktopPanel(panel, trigger, fromSwitch = false) {
      if (!panel || !trigger || !this.desktopQuery.matches) return;

      this.cancelHoverIntent();
      this.cancelDesktopClose();
      this.classList.remove("is-panel-closing");

      if (!fromSwitch && this.pendingPanel) {
        this.pendingPanel = panel;
        this.pendingTrigger = trigger;
        this.pendingNavigationSwitch = Boolean(
          this.outgoingPanel &&
            this.panels.includes(this.outgoingPanel) &&
            this.panels.includes(panel),
        );
        this.classList.toggle(
          "is-navigation-switching",
          this.pendingNavigationSwitch,
        );
        this.lastTrigger = trigger;
        return;
      }

      const previous = this.activePanel;
      if (previous === panel) {
        this.classList.remove("is-panel-switching", "is-navigation-switching");
        panel.classList.add("is-active");
        this.setDesktopShellHeight(this.measurePanel(panel));
        if (panel === this.searchPanel) this.focusSearchInput();
        return;
      }

      if (previous && !fromSwitch) {
        this.beginDesktopPanelSwitch(previous, panel, trigger);
        return;
      }

      window.clearTimeout(this.switchTimer);
      window.clearTimeout(this.switchCleanupTimer);
      const isSwitch = fromSwitch;
      const isNavigationSwitch = Boolean(
        fromSwitch && this.pendingNavigationSwitch,
      );
      const headerHeight = this.desktopHeight();
      const panelWasVisible = !panel.hidden;
      const panelCurrentHeight = fromSwitch
        ? this.switchFromHeight || headerHeight
        : panelWasVisible
          ? panel.getBoundingClientRect().height
          : headerHeight;

      this.panelTriggers.forEach((item) => {
        item.setAttribute("aria-expanded", "false");
      });
      this.searchTrigger?.setAttribute("aria-expanded", "false");
      this.syncBagTriggerMode(false);
      trigger.setAttribute("aria-expanded", "true");

      this.cancelPanelHide(panel);
      panel.hidden = false;
      panel.classList.remove("is-active", "is-switching-out");
      const targetHeight = this.measurePanel(panel);
      panel.style.height = `${panelCurrentHeight || headerHeight}px`;
      panel.getBoundingClientRect();

      this.activePanel = panel;
      this.outgoingPanel = null;
      this.pendingPanel = null;
      this.pendingTrigger = null;
      this.lastTrigger = trigger;
      this.classList.toggle("is-panel-switching", isSwitch);
      this.classList.toggle("is-navigation-switching", isNavigationSwitch);

      if (!this.classList.contains("is-panel-open")) {
        this.setDesktopShellHeight(headerHeight);
      }

      window.requestAnimationFrame(() => {
        if (this.activePanel !== panel) return;
        this.classList.add("is-panel-open");
        panel.classList.add("is-active");
        this.setDesktopShellHeight(targetHeight);
        panel.getBoundingClientRect();
        window.requestAnimationFrame(() => {
          if (this.activePanel !== panel) return;
          panel.style.removeProperty("height");
          if (panel === this.searchPanel) this.focusSearchInput();
        });
      });

      if (isSwitch) {
        this.switchCleanupTimer = window.setTimeout(() => {
          if (this.activePanel === panel) {
            this.classList.remove(
              "is-panel-switching",
              "is-navigation-switching",
            );
            this.pendingNavigationSwitch = false;
            this.switchFromHeight = null;
          }
        }, this.duration(this.timings.desktopSwitchSettle));
      }

      this.showCurtain();
    }

    beginDesktopPanelSwitch(previous, panel, trigger) {
      window.clearTimeout(this.switchTimer);
      window.clearTimeout(this.switchCleanupTimer);
      const previousHeight =
        previous.getBoundingClientRect().height || this.desktopHeight();

      this.panelTriggers.forEach((item) => {
        item.setAttribute("aria-expanded", "false");
      });
      this.searchTrigger?.setAttribute("aria-expanded", "false");
      this.syncBagTriggerMode(false);

      previous.style.height = `${previousHeight}px`;
      previous.classList.remove("is-active");
      previous.classList.add("is-switching-out");
      if (previous === this.searchPanel) this.resetSearchSession(false);

      this.activePanel = null;
      this.outgoingPanel = previous;
      this.pendingPanel = panel;
      this.pendingTrigger = trigger;
      this.pendingNavigationSwitch = Boolean(
        this.panels.includes(previous) && this.panels.includes(panel),
      );
      this.switchFromHeight = previousHeight;
      this.lastTrigger = trigger;
      this.classList.add("is-panel-open", "is-panel-switching");
      this.classList.toggle(
        "is-navigation-switching",
        this.pendingNavigationSwitch,
      );
      this.setDesktopShellHeight(previousHeight);
      this.showCurtain();

      this.switchTimer = window.setTimeout(() => {
        const nextPanel = this.pendingPanel;
        const nextTrigger = this.pendingTrigger;
        const outgoing = this.outgoingPanel;
        if (!nextPanel || !nextTrigger || !this.desktopQuery.matches) return;

        if (outgoing) this.schedulePanelHide(outgoing, true);
        this.outgoingPanel = null;
        this.showDesktopPanel(nextPanel, nextTrigger, true);
      }, this.duration(this.timings.desktopSwitchGap));
    }

    showCurtain() {
      if (!this.curtain) return;
      if (this.curtain.hidden) {
        this.curtain.hidden = false;
        this.curtain.getBoundingClientRect();
      }
      window.requestAnimationFrame(() => {
        if (
          this.activePanel ||
          this.outgoingPanel ||
          this.pendingPanel ||
          this.classList.contains("is-mobile-open") ||
          this.isMobileUtilityOpen()
        ) {
          this.curtain.classList.add("is-active");
        }
      });
    }

    measurePanel(panel) {
      const headerHeight = this.desktopHeight();
      if (!panel) return headerHeight;
      const clip = panel.querySelector(".cm-header-panel__clip");
      if (!clip) return headerHeight;
      const available = Math.max(0, window.innerHeight - headerHeight);
      const contentHeight = Math.min(clip.scrollHeight, available);
      const openHeight = headerHeight + contentHeight;
      panel.style.setProperty("--cm-panel-open-height", `${openHeight}px`);
      return openHeight;
    }

    setDesktopShellHeight(height) {
      const headerHeight = this.desktopHeight();
      this.desktopPanels?.style.setProperty(
        "--cm-panel-shell-height",
        `${Math.max(headerHeight, height || headerHeight)}px`,
      );
    }

    updateActivePanelHeight() {
      window.requestAnimationFrame(() => {
        if (this.desktopQuery.matches && this.activePanel) {
          this.setDesktopShellHeight(this.measurePanel(this.activePanel));
        }
      });
    }

    cancelDesktopClose() {
      window.clearTimeout(this.closeTimer);
    }

    cancelPanelHide(panel) {
      const timer = this.panelHideTimers.get(panel);
      if (timer) window.clearTimeout(timer);
      this.panelHideTimers.delete(panel);
    }

    schedulePanelHide(panel, immediate = false, delay = 400) {
      this.cancelPanelHide(panel);
      const hide = () => {
        if (immediate || panel !== this.activePanel) {
          panel.hidden = true;
          panel.classList.remove("is-switching-out", "is-active");
          panel.style.removeProperty("height");
        }
        this.panelHideTimers.delete(panel);
      };
      if (immediate || this.reducedMotionQuery.matches) {
        hide();
        return;
      }
      const timer = window.setTimeout(hide, this.duration(delay));
      this.panelHideTimers.set(panel, timer);
    }

    closeDesktopPanel(restoreFocus, immediate = false) {
      if (this.isMobileUtilityOpen()) {
        this.closeMobileUtility(restoreFocus, immediate);
        return;
      }

      this.cancelHoverIntent();
      this.cancelDesktopClose();
      window.clearTimeout(this.switchTimer);
      window.clearTimeout(this.switchCleanupTimer);
      this.classList.remove("is-panel-switching", "is-navigation-switching");
      const panelToClose = this.activePanel || this.outgoingPanel;
      if (panelToClose) {
        panelToClose.style.removeProperty("height");
        panelToClose.classList.remove("is-active");
        panelToClose.classList.add("is-switching-out");
        this.schedulePanelHide(panelToClose, immediate);
        this.classList.add("is-panel-closing");
      }
      this.setDesktopShellHeight(this.desktopHeight());
      this.curtain?.classList.remove("is-active");

      this.panelTriggers.forEach((item) => {
        item.setAttribute("aria-expanded", "false");
      });
      this.searchTrigger?.setAttribute("aria-expanded", "false");
      this.syncBagTriggerMode(false);
      this.searchInput?.setAttribute("aria-expanded", "false");
      this.activePanel = null;
      this.outgoingPanel = null;
      this.pendingPanel = null;
      this.pendingTrigger = null;
      this.pendingNavigationSwitch = false;
      this.switchFromHeight = null;

      if (panelToClose === this.searchPanel) {
        this.resetSearchSession(false);
      }

      const finish = () => {
        if (!this.activePanel && !this.outgoingPanel && !this.pendingPanel) {
          this.classList.remove("is-panel-open", "is-panel-closing");
          this.desktopPanels?.style.removeProperty("--cm-panel-shell-height");
          if (this.curtain) this.curtain.hidden = true;
        }
      };
      window.clearTimeout(this.closeTimer);
      if (immediate || this.reducedMotionQuery.matches) {
        finish();
      } else {
        this.closeTimer = window.setTimeout(
          finish,
          this.duration(this.timings.desktopClose),
        );
      }

      if (restoreFocus) this.lastTrigger?.focus();
    }

    focusSearchInput() {
      if (
        this.activePanel !== this.searchPanel ||
        this.searchPanel?.hidden ||
        !this.searchPanel?.classList.contains("is-active")
      ) {
        return;
      }

      this.searchInput?.focus({ preventScroll: true });
    }

    syncSearchClear() {
      if (!this.searchClear) return;
      this.searchClear.hidden = !this.searchInput?.value;
    }

    clearSearchQuery() {
      if (!this.searchInput) return;

      window.clearTimeout(this.searchDebounceTimer);
      this.searchInput.value = "";
      this.syncSearchClear();
      this.clearPredictiveResults(true, true);
      this.searchInput.focus({ preventScroll: true });
    }

    resetSearchSession(updateHeight = true) {
      window.clearTimeout(this.searchDebounceTimer);
      if (this.searchInput) this.searchInput.value = "";
      this.syncSearchClear();
      this.clearPredictiveResults(true, true, updateHeight);
    }

    invalidateSearchRequest() {
      this.searchRequestId += 1;
      this.searchAbortController?.abort();
      this.searchAbortController = null;
      return this.searchRequestId;
    }

    async getPredictiveResults(searchTerm) {
      if (!this.storefrontSearchUrl || !this.searchResults) return;
      if (
        this.activePanel !== this.searchPanel ||
        this.searchInput?.value.trim() !== searchTerm
      ) {
        return;
      }

      const cacheKey = searchTerm.toLocaleLowerCase();
      if (this.searchCache.has(cacheKey)) {
        this.renderPredictiveResults(
          this.searchCache.get(cacheKey),
          searchTerm,
        );
        return;
      }

      const requestId = this.invalidateSearchRequest();
      const controller = new AbortController();
      this.searchAbortController = controller;
      const requestUrl = new URL(
        this.storefrontSearchUrl,
        window.location.origin,
      );
      const query = `
        query PredictiveSearch($query: String!) {
          predictiveSearch(
            query: $query
            limit: 6
            limitScope: EACH
            types: [PRODUCT, COLLECTION, QUERY]
            unavailableProducts: LAST
            searchableFields: [TITLE, PRODUCT_TYPE, VENDOR, VARIANTS_TITLE]
          ) {
            products {
              title
              onlineStoreUrl
              trackingParameters
              featuredImage {
                url
              }
            }
            collections {
              title
              onlineStoreUrl
              trackingParameters
            }
            queries {
              text
            }
          }
          productSearch: search(
            query: $query
            first: 20
            prefix: LAST
            types: [PRODUCT]
            unavailableProducts: LAST
          ) {
            nodes {
              ... on Product {
                title
                onlineStoreUrl
                trackingParameters
                featuredImage {
                  url
                }
              }
            }
          }
        }
      `;

      try {
        const response = await fetch(requestUrl, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query,
            variables: { query: searchTerm },
          }),
          signal: controller.signal,
        });
        if (!response.ok)
          throw new Error(`Predictive search ${response.status}`);

        const responseData = await response.json();
        if (responseData.errors?.length) {
          throw new Error(responseData.errors[0].message);
        }

        const predictiveSearch = responseData.data?.predictiveSearch;
        if (!predictiveSearch) throw new Error("Predictive search unavailable");

        const searchUrl =
          this.searchTrigger?.dataset.searchUrl || "/search";
        const queryResults = (predictiveSearch.queries || []).map((result) => {
          const url = new URL(searchUrl, window.location.origin);
          url.searchParams.set("q", result.text);
          url.searchParams.set("options[prefix]", "last");
          return {
            text: result.text,
            url: `${url.pathname}${url.search}`,
          };
        });
        // Predictive search doesn't backfill slots removed by channel
        // filtering, so supplement it with the channel-aware product search.
        const products = [];
        const productUrls = new Set();
        [
          ...(predictiveSearch.products || []),
          ...(responseData.data?.productSearch?.nodes || []),
        ].forEach((product) => {
          if (
            !product.onlineStoreUrl ||
            productUrls.has(product.onlineStoreUrl) ||
            products.length >= 6
          ) {
            return;
          }
          productUrls.add(product.onlineStoreUrl);
          products.push({
            title: product.title,
            url: this.storefrontResultUrl(product),
            featured_image: product.featuredImage,
          });
        });
        const data = {
          resources: {
            results: {
              products,
              collections: (predictiveSearch.collections || [])
                .filter((collection) => collection.onlineStoreUrl)
                .map((collection) => ({
                  title: collection.title,
                  url: this.storefrontResultUrl(collection),
                })),
              queries: queryResults,
            },
          },
        };
        if (
          controller.signal.aborted ||
          requestId !== this.searchRequestId ||
          this.activePanel !== this.searchPanel ||
          this.searchInput?.value.trim() !== searchTerm
        ) {
          return;
        }

        this.searchCache.set(cacheKey, data);
        this.renderPredictiveResults(data, searchTerm);
      } catch (error) {
        if (
          error.name === "AbortError" ||
          controller.signal.aborted ||
          requestId !== this.searchRequestId ||
          this.activePanel !== this.searchPanel
        ) {
          return;
        }
        this.clearPredictiveResults(false, false);
        this.setSearchStatus(
          "Suggestions are unavailable. Press Enter to search the store.",
        );
      } finally {
        if (this.searchAbortController === controller) {
          this.searchAbortController = null;
        }
      }
    }

    renderPredictiveResults(data, searchTerm) {
      if (!this.searchResults || !this.searchInput) return;

      const resources = data?.resources?.results || {};
      const products = Array.isArray(resources.products)
        ? resources.products.slice(0, 6)
        : [];
      const collections = Array.isArray(resources.collections)
        ? resources.collections.slice(0, 3)
        : [];
      const queries = Array.isArray(resources.queries)
        ? resources.queries.slice(0, 3)
        : [];

      this.searchResults.replaceChildren();
      this.resetSearchSelection();
      let optionIndex = 0;

      if (products.length) {
        const group = this.createResultsGroup("Products");
        const list = document.createElement("ul");
        list.className = "cm-header-search-results__products";
        list.setAttribute("role", "list");

        products.forEach((product) => {
          const item = document.createElement("li");
          const link = document.createElement("a");
          link.className = "cm-header-search-results__product";
          link.href = product.url;
          optionIndex = this.prepareSearchOption(link, optionIndex);

          const imageUrl = product.featured_image?.url;
          if (imageUrl) {
            const image = document.createElement("img");
            image.className = "cm-header-search-results__image";
            image.src = imageUrl;
            image.alt = "";
            image.width = 44;
            image.height = 44;
            image.loading = "lazy";
            link.append(image);
          } else {
            const placeholder = document.createElement("span");
            placeholder.className =
              "cm-header-search-results__image-placeholder";
            placeholder.setAttribute("aria-hidden", "true");
            link.append(placeholder);
          }

          const title = document.createElement("span");
          title.className = "cm-header-search-results__product-title";
          title.textContent = product.title;
          link.append(title);
          item.append(link);
          list.append(item);
        });

        group.append(list);
        this.searchResults.append(group);
      }

      const relatedLinks = [...queries, ...collections].slice(0, 4);
      if (relatedLinks.length) {
        const group = this.createResultsGroup("Related searches");
        const list = document.createElement("ul");
        list.className = "cm-header-search-results__links";
        list.setAttribute("role", "list");

        relatedLinks.forEach((result) => {
          const item = document.createElement("li");
          const link = document.createElement("a");
          link.className = "cm-header-search-results__link";
          link.href = result.url;
          optionIndex = this.prepareSearchOption(link, optionIndex);
          link.append(this.createArrowIcon());
          const label = document.createElement("span");
          label.textContent = result.text || result.title;
          link.append(label);
          item.append(link);
          list.append(item);
        });

        group.append(list);
        this.searchResults.append(group);
      }

      if (!products.length && !relatedLinks.length) {
        this.clearPredictiveResults(false, false);
        this.setSearchStatus(
          `No suggestions for “${searchTerm}”. Press Enter to search the store.`,
        );
        return;
      }

      const allResultsLink = document.createElement("a");
      allResultsLink.className = "cm-header-search-results__all";
      allResultsLink.href = `${
        this.searchTrigger?.dataset.searchUrl || "/search"
      }?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last`;
      allResultsLink.textContent = `View all results for “${searchTerm}”`;
      this.prepareSearchOption(allResultsLink, optionIndex);
      this.searchResults.append(allResultsLink);

      if (this.searchDefault) this.searchDefault.hidden = true;
      this.searchResults.hidden = false;
      this.searchInput.setAttribute("aria-expanded", "true");
      this.setSearchStatus("");
      this.updateActivePanelHeight();
    }

    storefrontResultUrl(result) {
      const url = new URL(result.onlineStoreUrl, window.location.origin);
      new URLSearchParams(result.trackingParameters || "").forEach(
        (value, key) => url.searchParams.set(key, value),
      );
      return url.href;
    }

    prepareSearchOption(link, index) {
      link.id = `CmSearchOption-${this.dataset.sectionId}-${index}`;
      link.setAttribute("role", "option");
      link.setAttribute("aria-selected", "false");
      return index + 1;
    }

    createArrowIcon() {
      const namespace = "http://www.w3.org/2000/svg";
      const svg = document.createElementNS(namespace, "svg");
      svg.setAttribute("aria-hidden", "true");
      svg.setAttribute("viewBox", "0 0 9 16");
      const path = document.createElementNS(namespace, "path");
      path.setAttribute(
        "d",
        "m8.61 8.1-2.99 2.99a.5.5 0 0 1-.71-.71l2.14-2.13H.74a.5.5 0 0 1 0-1h6.31L4.91 5.11a.5.5 0 0 1 .71-.71l2.99 2.99a.5.5 0 0 1 0 .71Z",
      );
      svg.append(path);
      return svg;
    }

    createResultsGroup(headingText) {
      const group = document.createElement("div");
      group.className = "cm-header-search-results__group";
      const heading = document.createElement("p");
      heading.className = "cm-header-search-results__heading";
      heading.textContent = headingText;
      group.append(heading);
      return group;
    }

    getSearchOptions() {
      return this.searchResults
        ? [...this.searchResults.querySelectorAll('[role="option"]')]
        : [];
    }

    moveSearchSelection(direction) {
      const options = this.getSearchOptions();
      if (!options.length) return;
      const next =
        this.searchActiveIndex < 0
          ? direction > 0
            ? 0
            : options.length - 1
          : (this.searchActiveIndex + direction + options.length) %
            options.length;
      this.setSearchSelection(next, options);
    }

    setSearchSelection(index, options = this.getSearchOptions()) {
      options.forEach((option, optionIndex) => {
        const selected = optionIndex === index;
        option.classList.toggle("is-selected", selected);
        option.setAttribute("aria-selected", String(selected));
      });
      this.searchActiveIndex = index;
      const active = options[index];
      if (active) {
        this.searchInput?.setAttribute("aria-activedescendant", active.id);
        active.scrollIntoView({ block: "nearest" });
      }
    }

    resetSearchSelection() {
      this.searchActiveIndex = -1;
      this.searchInput?.removeAttribute("aria-activedescendant");
      this.getSearchOptions().forEach((option) => {
        option.classList.remove("is-selected");
        option.setAttribute("aria-selected", "false");
      });
    }

    clearPredictiveResults(
      clearStatus = true,
      showDefault = false,
      updateHeight = true,
    ) {
      this.invalidateSearchRequest();
      this.resetSearchSelection();

      if (this.searchResults) {
        this.searchResults.hidden = true;
        this.searchResults.replaceChildren();
      }
      if (this.searchDefault) this.searchDefault.hidden = !showDefault;
      this.searchInput?.setAttribute("aria-expanded", "false");
      if (clearStatus) this.setSearchStatus("", false);
      if (updateHeight) this.updateActivePanelHeight();
    }

    setSearchStatus(message, updateHeight = true) {
      if (!this.searchStatus) return;
      this.searchStatus.textContent = message;
      this.searchStatus.hidden = !message;
      if (updateHeight) this.updateActivePanelHeight();
    }

    toggleMobileMenu() {
      if (this.classList.contains("is-mobile-open")) {
        this.closeMobileMenu(true);
      } else {
        this.openMobileMenu();
      }
    }

    openMobileMenu() {
      if (!this.mobileMenu || !this.mobileToggle || this.desktopQuery.matches) {
        return;
      }

      this.closeDesktopPanel(false, true);
      this.closeMobileUtility(false, true);
      window.clearTimeout(this.mobileTimer);
      this.openMobileRoot(false, true);
      this.mobileMenu.hidden = false;
      this.mobileMenu.removeAttribute("inert");
      this.mobileMenu.setAttribute("aria-hidden", "false");
      this.mobileMenu.getBoundingClientRect();
      this.getActiveMobileLevel()?.scrollTo({ top: 0, left: 0 });
      this.setMobileToggleState(
        this.mobileMenu.id,
        "Close navigation menu",
        true,
      );
      this.setScrollLock(true);

      window.requestAnimationFrame(() => {
        this.classList.add("is-mobile-open");
        this.showCurtain();
      });
      this.mobileTimer = window.setTimeout(() => {
        this.getActiveMobileLevel()
          ?.querySelector("a[href], button:not([disabled])")
          ?.focus();
      }, this.duration(this.timings.mobileFocus));
    }

    closeMobileMenu(restoreFocus, immediate = false) {
      if (!this.mobileMenu || !this.mobileToggle) return;
      const wasOpen = this.classList.contains("is-mobile-open");
      window.clearTimeout(this.mobileTimer);
      window.clearTimeout(this.mobileBackTimer);
      this.clearMobileLevelTransition();
      if (restoreFocus && wasOpen) {
        this.mobileToggle.focus({ preventScroll: true });
      }
      this.classList.remove("is-mobile-open", "is-mobile-submenu-open");
      this.mobileMenu.setAttribute("inert", "");
      if (!this.isMobileUtilityOpen()) this.setScrollLock(false);
      this.curtain?.classList.remove("is-active");

      const finish = () => {
        this.mobileMenu.hidden = true;
        this.mobileMenu.setAttribute("aria-hidden", "true");
        if (this.mobileBack) this.mobileBack.hidden = true;
        this.setMobileRootState();
        this.setMobileToggleState(
          this.mobileMenuId,
          "Open navigation menu",
          false,
        );
        if (this.curtain && !this.isMobileUtilityOpen()) {
          this.curtain.hidden = true;
        }
      };

      if (immediate || this.mobileMenu.hidden) {
        finish();
      } else {
        this.mobileTimer = window.setTimeout(
          finish,
          this.duration(this.timings.mobileShell),
        );
      }
    }

    openMobileLevel(button) {
      const targetId = button.getAttribute("data-cm-mobile-open");
      const target = this.mobileLevels.find(
        (level) => level.dataset.cmMobileLevel === targetId,
      );
      if (!target) return;

      window.clearTimeout(this.mobileBackTimer);
      target.scrollTo({ top: 0, left: 0 });
      this.mobileReturnButton?.setAttribute("aria-expanded", "false");
      this.mobileReturnButton = button;
      button.setAttribute("aria-expanded", "true");
      if (this.mobileBack) this.mobileBack.hidden = false;
      this.classList.add("is-mobile-submenu-open");
      this.transitionMobileLevel(target, "forward", () => {
        if (this.mobileReturnButton === button) {
          target.querySelector("a[href], button:not([disabled])")?.focus();
        }
      });
    }

    openMobileRoot(restoreFocus, immediate = false) {
      const root = this.mobileLevels?.find(
        (level) => level.dataset.cmMobileLevel === "root",
      );
      if (!root) return;

      window.clearTimeout(this.mobileBackTimer);
      const returnButton = this.mobileReturnButton;
      returnButton?.setAttribute("aria-expanded", "false");
      this.classList.remove("is-mobile-submenu-open");

      if (immediate) {
        this.setActiveMobileLevel(root);
      } else {
        this.transitionMobileLevel(root, "back", () => {
          if (restoreFocus) returnButton?.focus();
        });
      }

      const hideBack = () => {
        if (this.mobileBack) this.mobileBack.hidden = true;
      };
      if (immediate) {
        hideBack();
      } else {
        this.mobileBackTimer = window.setTimeout(hideBack, this.duration(240));
      }

      if (restoreFocus && immediate) returnButton?.focus();
    }

    setMobileRootState() {
      const root = this.mobileLevels?.find(
        (level) => level.dataset.cmMobileLevel === "root",
      );
      if (root) this.setActiveMobileLevel(root);
      this.mobileReturnButton?.setAttribute("aria-expanded", "false");
      this.mobileReturnButton = null;
    }

    setActiveMobileLevel(target) {
      this.clearMobileLevelTransition();
      this.mobileLevels?.forEach((level) => {
        const isTarget = level === target;
        level.classList.toggle("is-active", isTarget);
        level.setAttribute("aria-hidden", String(!isTarget));
        level.toggleAttribute("inert", !isTarget);
      });
    }

    transitionMobileLevel(target, direction, onComplete) {
      const outgoing = this.getActiveMobileLevel();
      if (!outgoing || outgoing === target || this.reducedMotionQuery.matches) {
        this.setActiveMobileLevel(target);
        onComplete?.();
        return;
      }

      this.clearMobileLevelTransition();
      if (direction === "forward" && outgoing.contains(document.activeElement)) {
        this.mobileBack?.focus({ preventScroll: true });
      }
      this.mobileLevels.forEach((level) => {
        const isTarget = level === target;
        level.setAttribute("aria-hidden", String(!isTarget));
        level.toggleAttribute("inert", !isTarget);
      });
      outgoing.classList.add("is-mobile-level-outgoing");
      target.classList.add("is-mobile-level-incoming");
      this.dataset.cmMobileDirection = direction;
      target.getBoundingClientRect();
      this.classList.add("is-mobile-level-transitioning");

      this.mobileLevelTimer = window.setTimeout(() => {
        this.setActiveMobileLevel(target);
        onComplete?.();
      }, this.duration(this.timings.mobileLevel));
    }

    clearMobileLevelTransition() {
      window.clearTimeout(this.mobileLevelTimer);
      this.mobileLevelTimer = null;
      this.classList.remove("is-mobile-level-transitioning");
      this.removeAttribute("data-cm-mobile-direction");
      this.mobileLevels?.forEach((level) => {
        level.classList.remove(
          "is-mobile-level-outgoing",
          "is-mobile-level-incoming",
        );
      });
    }

    getActiveMobileLevel() {
      return this.mobileLevels?.find((level) =>
        level.classList.contains("is-active"),
      );
    }

    isMobileUtilityOpen() {
      return Boolean(this.getMobileUtilityPanel());
    }

    getMobileUtilityPanel() {
      if (this.classList.contains("is-mobile-search-open")) {
        return this.searchPanel;
      }
      if (this.classList.contains("is-mobile-bag-open")) {
        return this.bagPanel;
      }
      if (
        !this.desktopQuery.matches &&
        (this.activePanel === this.searchPanel ||
          this.activePanel === this.bagPanel) &&
        !this.activePanel?.hidden
      ) {
        return this.activePanel;
      }
      return null;
    }

    setMobileToggleState(controls, label, expanded) {
      if (!this.mobileToggle) return;
      if (controls) this.mobileToggle.setAttribute("aria-controls", controls);
      this.mobileToggle.setAttribute("aria-label", label);
      this.mobileToggle.setAttribute("aria-expanded", String(expanded));
    }

    openMobileSearch() {
      this.openMobileUtility(
        this.searchPanel,
        this.searchTrigger,
        "search",
        "Close search",
      );
    }

    openMobileBag() {
      this.openMobileUtility(
        this.bagPanel,
        this.bagTrigger,
        "bag",
        "Close Bag",
      );
    }

    openMobileUtility(panel, trigger, kind, closeLabel) {
      if (
        !panel ||
        !trigger ||
        !this.mobileToggle ||
        this.desktopQuery.matches
      ) {
        return;
      }

      this.closeDesktopPanel(false, true);
      this.closeMobileMenu(false, true);
      if (this.isMobileUtilityOpen()) this.closeMobileUtility(false, true);
      window.clearTimeout(this.mobileTimer);

      this.cancelPanelHide(panel);
      panel.hidden = false;
      panel.removeAttribute("inert");
      panel.setAttribute("aria-hidden", "false");
      panel.setAttribute("role", "dialog");
      panel.setAttribute("aria-modal", "true");
      panel.classList.remove("is-active", "is-switching-out");
      panel.style.height = `${this.mobileHeight()}px`;
      this.classList.remove("is-mobile-utility-closing");
      panel.getBoundingClientRect();

      this.activePanel = panel;
      this.lastTrigger = trigger;
      this.panelTriggers.forEach((item) => {
        item.setAttribute("aria-expanded", "false");
      });
      this.searchTrigger?.setAttribute("aria-expanded", "false");
      this.syncBagTriggerMode(false);
      trigger.setAttribute("aria-expanded", "true");
      this.setMobileToggleState(panel.id, closeLabel, true);
      this.setScrollLock(true);

      const openClass = `is-mobile-${kind}-open`;
      window.requestAnimationFrame(() => {
        if (this.activePanel !== panel) return;
        this.classList.add("is-panel-open", openClass);
        panel.classList.add("is-active");
        panel.querySelector(".cm-header-panel__clip")?.scrollTo({
          top: 0,
          left: 0,
        });
        this.showCurtain();
        panel.getBoundingClientRect();
        window.requestAnimationFrame(() =>
          panel.style.removeProperty("height"),
        );
      });

      this.mobileTimer = window.setTimeout(() => {
        if (this.activePanel !== panel) return;
        const focusTarget =
          panel === this.searchPanel
            ? this.searchInput
            : panel.querySelector("a[href], button:not([disabled])");
        focusTarget?.focus({ preventScroll: true });
      }, this.duration(this.timings.mobileFocus));
    }

    closeMobileSearch(restoreFocus, immediate = false) {
      this.closeMobileUtility(restoreFocus, immediate, this.searchPanel);
    }

    closeMobileBag(restoreFocus, immediate = false) {
      this.closeMobileUtility(restoreFocus, immediate, this.bagPanel);
    }

    closeMobileUtility(restoreFocus, immediate = false, requestedPanel = null) {
      const panel = requestedPanel || this.getMobileUtilityPanel();
      if (!panel) return;

      const isSearch = panel === this.searchPanel;
      const openClass = isSearch
        ? "is-mobile-search-open"
        : "is-mobile-bag-open";
      const wasOpen = this.classList.contains(openClass);
      const trigger = isSearch ? this.searchTrigger : this.bagTrigger;
      window.clearTimeout(this.mobileTimer);
      if (isSearch) this.resetSearchSession(false);
      if (restoreFocus && wasOpen) {
        this.mobileToggle?.focus({ preventScroll: true });
      }
      trigger?.setAttribute("aria-expanded", "false");
      this.searchInput?.setAttribute("aria-expanded", "false");
      panel.toggleAttribute("inert", wasOpen);

      if (wasOpen && !immediate) {
        this.classList.add("is-mobile-utility-closing");
        panel.style.height = `${this.mobileHeight()}px`;
      }
      if (!this.classList.contains("is-mobile-open")) this.setScrollLock(false);
      this.curtain?.classList.remove("is-active");

      const finish = () => {
        panel.classList.remove("is-active", "is-switching-out");
        panel.hidden = true;
        panel.removeAttribute("inert");
        panel.removeAttribute("aria-hidden");
        panel.setAttribute("role", "region");
        panel.removeAttribute("aria-modal");
        panel.style.removeProperty("height");
        this.classList.remove(
          openClass,
          "is-mobile-utility-closing",
          "is-panel-open",
        );
        if (this.activePanel === panel) this.activePanel = null;
        this.setMobileToggleState(
          this.mobileMenuId,
          "Open navigation menu",
          false,
        );
        if (this.curtain && !this.classList.contains("is-mobile-open")) {
          this.curtain.hidden = true;
        }
        if (restoreFocus && wasOpen) {
          trigger?.focus({ preventScroll: true });
        }
      };

      if (immediate || !wasOpen) {
        finish();
      } else {
        this.mobileTimer = window.setTimeout(
          finish,
          this.duration(this.timings.mobileShell),
        );
      }
    }

    trapMobileFocus(event) {
      const container = this.isMobileUtilityOpen()
        ? this.getMobileUtilityPanel()
        : this.getActiveMobileLevel();
      if (!container) return;

      const candidates = [
        this.mobileBack,
        ...container.querySelectorAll(
          "a[href], button:not([disabled]), input:not([disabled])",
        ),
        this.mobileToggle,
      ].filter((element) => this.isVisible(element));
      if (!candidates.length) return;

      const first = candidates[0];
      const last = candidates[candidates.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    isVisible(element) {
      return Boolean(
        element &&
        !element.hidden &&
        !element.closest("[inert]") &&
        element.getClientRects().length &&
        window.getComputedStyle(element).visibility !== "hidden",
      );
    }
  }

  customElements.define("collect-monsters-header", CollectMonstersHeader);
}
